import { ALL_COUNTRY_CODES, ALL_COUNTRY_KEY, ALL_LANGUAGE_CODE } from '@/utils/country/country-codes'
import { AllLanguageCodes } from '@/model/Language'
import { getStoreName } from '@/base/MaUtils.js'
import maDate from '@/base/MaDate'
import Store from '@/base/constants/Store.js'
import { SAFE_VALUES } from '@/base/constants/SafeValues'
import { Query } from '@/base/constants/route'
import { Device, StoreDevicesWithAll } from '@/base/constants/Device.js'

/**
 * Composable for validating query parameters against a set of predefined rules.
 * This function provides validation for various query parameters used in the application.
 * It ensures that the values passed for each parameter meet the expected format,
 * defaulting to safe values if they do not.
 *
 * @returns {object} An object containing the `routeParamsValidator` method.
 */
export function useQueryParamsValidator() {
  const isValidCountryCode = countryCode => (ALL_COUNTRY_CODES.includes(countryCode) || countryCode === ALL_COUNTRY_KEY)

  /**
   * Object containing validation functions for different query parameters.
   * Each property corresponds to a query parameter and maps to a function that validates its value.
   * If the value does not meet the expected criteria, a predefined safe value is returned.
   *
   */
  const routeParamsValidator = {

    [Query.COUNTRY_CODE]: value => isValidCountryCode(value) ? value : SAFE_VALUES.COUNTRY_CODE,

    [Query.COUNTRY_CODES]: (value) => {
      if (Array.isArray(value) && !value.every(countryCode => isValidCountryCode(countryCode))) {
        return SAFE_VALUES.countryCodes
      }
      return value
    },

    [Query.STORE]: (value, allParams = {}) => {
      if (!Object.values(Store).includes(value)) {
        return SAFE_VALUES.store
      }
      if (allParams?.publisherId && getStoreName(allParams.publisherId) !== value) {
        return getStoreName(allParams.publisherId)
      }
      return value
    },

    [Query.DEVICE]: (value, allParams = {}) => {
      const store = allParams?.store || (allParams?.trackId ? getStoreName(allParams.trackId) : null)
      if (
        !Object.values(Device).includes(value)
        || (store && !StoreDevicesWithAll[store].includes(value))
      ) {
        return StoreDevicesWithAll[store][0]
      }
      return value
    },

    [Query.DATE]: (value) => {
      if (!maDate(value).isValid()) {
        return SAFE_VALUES.date
      }
      return value
    },

    [Query.END_DATE]: (value) => {
      if (!maDate(value).isValid()) {
        return SAFE_VALUES.endDate
      }
      return value
    },

    [Query.SELECTED_DATE]: (value) => {
      if (!maDate(value).isValid()) {
        return SAFE_VALUES.date
      }
      return value
    },

    [Query.START_DATE]: (value, allParams = {}) => {
      if (!maDate(value).isValid()) {
        return SAFE_VALUES.startDate
      }
      if (
        allParams?.[Query.END_DATE]
        && (maDate(allParams[Query.END_DATE]).diff(allParams[Query.START_DATE], 'days') < 0)
      ) {
        return maDate(allParams[Query.END_DATE]).formatISODate()
      }
      return value
    },

    [Query.LANGUAGE_CODE]: (value) => {
      const languageCodesIncludingAll = AllLanguageCodes.slice().concat(ALL_LANGUAGE_CODE)
      if (!languageCodesIncludingAll.includes(value?.toLowerCase())) {
        return SAFE_VALUES.languageCode
      }
      return value
    },

    [Query.MONTH]: (value) => {
      if (Number.isNaN(Number.parseInt(value)) || Number.parseInt(value) < 1 || Number.parseInt(value) > 12) {
        return SAFE_VALUES.month
      }
      return value
    },

    [Query.YEAR]: (value) => {
      if (Number.isNaN(Number.parseInt(value)) || value.length !== 4) {
        return SAFE_VALUES.year
      }
      return value
    },

  }

  return {
    routeParamsValidator,
  }
}
